import { BE_ACCESS_TOKEN_LOCALSTORAGE_KEY } from 'hooks/useCurrentUser'

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL

function getAuthToken(): string {
  const credentials = JSON.parse(
    localStorage.getItem(BE_ACCESS_TOKEN_LOCALSTORAGE_KEY) || '{}'
  ).accessToken
  if (!credentials) {
    throw new Error('No credentials found')
  }
  return credentials
}

// Shape of one EVM marketplace listing, as returned by the BE
// /api/marketplace/evm/listings* endpoints.
// priceUsdc is the raw 6-decimal USDC integer (e.g. 15000000 = $15);
// priceUsd is the human-readable string ("15.00") for display.
// Buy/cancel only need tokenId — price is read by BE from the listing.
export interface EvmListing {
  id: string
  tokenId: number
  templateId: number
  seller: string
  priceUsdc: number
  priceUsd: string
  event: 'ForSale'
  txHash: string | null
  serial: number | null
  createdAt: string | null
  metadata: {
    name?: string
    image?: string
    thumbnail?: string
    tier?: string
    player?: string
    collectionName?: string
    headline?: string
    video?: string
  }
}

// Thrown when the BE returns a structured 403 indicating an on-chain
// pre-requisite has not been met. Callers can show a friendly snackbar
// instead of a raw error string. For wallets provisioned after the
// auto-approve backfill (Task #21) these should never fire — but legacy
// wallets that haven't been backfilled may still hit them.
export class MarketplaceApprovalError extends Error {
  constructor(
    public kind: 'nft-approval' | 'usdc-allowance',
    public detail: {
      error: string
      adminAddress?: string
      marketplaceAddress?: string
      requiredUsdc?: number
      currentAllowance?: string
    }
  ) {
    super(detail.error)
    this.name = 'MarketplaceApprovalError'
  }
}

// Thrown when the buyer simply does not have enough USDC. Distinct from
// MarketplaceApprovalError because the remedy is different: the wallet is set
// up correctly, it just needs topping up, so the caller should open the
// top-up flow rather than tell the user to contact support.
export class MarketplaceFundsError extends Error {
  constructor(
    public detail: {
      error: string
      requiredUsdc?: number
      balanceUsdc?: number
    }
  ) {
    super(detail.error)
    this.name = 'MarketplaceFundsError'
  }
}

export class MarketplaceApiError extends Error {
  constructor(public status: number, public body: any) {
    super(body?.error || `Marketplace API error (${status})`)
    this.name = 'MarketplaceApiError'
  }
}

async function parseError(response: Response): Promise<never> {
  let body: any = null
  try {
    body = await response.json()
  } catch {
    /* body wasn't JSON — fall through */
  }
  // 402 Payment Required — the BE checked the on-chain USDC balance and the
  // buyer is short. Surfaced as its own type so the caller can open the
  // top-up modal instead of showing a raw error.
  if (response.status === 402 || body?.kind === 'insufficient-funds') {
    throw new MarketplaceFundsError(
      body ?? { error: 'Insufficient USDC balance.' }
    )
  }
  if (response.status === 403 && body?.error) {
    const msg: string = body.error.toLowerCase()
    if (msg.includes('approved as operator') || msg.includes('not approved')) {
      throw new MarketplaceApprovalError('nft-approval', body)
    }
    if (msg.includes('usdc allowance') || msg.includes('insufficient usdc')) {
      throw new MarketplaceApprovalError('usdc-allowance', body)
    }
  }
  throw new MarketplaceApiError(response.status, body)
}

// ─── READ ───────────────────────────────────────────────────────────────────

// GET /api/marketplace/evm/listings  (public, no auth)
// Lists every active EVM marketplace listing, optionally filtered by templateId.
export async function getEvmListings(opts?: {
  limit?: number
  offset?: number
  templateId?: number
}): Promise<{
  total: number
  limit: number
  offset: number
  listings: EvmListing[]
}> {
  const params = new URLSearchParams()
  if (opts?.limit !== undefined) params.set('limit', String(opts.limit))
  if (opts?.offset !== undefined) params.set('offset', String(opts.offset))
  if (opts?.templateId !== undefined)
    params.set('templateId', String(opts.templateId))
  const qs = params.toString()

  const response = await fetch(
    `${BACKEND_URL}/api/marketplace/evm/listings${qs ? `?${qs}` : ''}`,
    { method: 'GET', cache: 'no-store' }
  )
  if (!response.ok) await parseError(response)
  return response.json()
}

// GET /api/marketplace/evm/listings/by-user/:wallet  (public, no auth)
// Lists active listings owned by a specific seller wallet — used by
// the FE "My Listings" page (Phase 3) to show what the current user
// has up for sale.
export async function getEvmListingsByUser(
  wallet: string
): Promise<{ wallet: string; listings: EvmListing[] }> {
  const response = await fetch(
    `${BACKEND_URL}/api/marketplace/evm/listings/by-user/${encodeURIComponent(
      wallet
    )}`,
    { method: 'GET', cache: 'no-store' }
  )
  if (!response.ok) await parseError(response)
  return response.json()
}

// ─── WRITE ──────────────────────────────────────────────────────────────────
// All three writes go through the admin signer on the BE — the call blocks
// 5–15s waiting for an on-chain confirmation before returning. Callers must
// put the submitting button into a loading/disabled state.

// POST /api/marketplace/evm/list  (auth required)
// priceUsdc is raw 6-decimal USDC units. Multiply human-dollar amounts by
// 1_000_000 before calling (e.g. $15 → 15000000). Passing the float dollar
// value here is a common bug and will list at $0.000015.
export async function listForSaleEvm(
  tokenId: number,
  priceUsdc: number
): Promise<{
  success: true
  tokenId: number
  priceUsdc: number
  txHash: string
}> {
  const response = await fetch(`${BACKEND_URL}/api/marketplace/evm/list`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      authorization: `Bearer ${getAuthToken()}`
    },
    body: JSON.stringify({ tokenId, priceUsdc })
  })
  if (!response.ok) await parseError(response)
  return response.json()
}

// POST /api/marketplace/evm/cancel  (auth required)
// Only the wallet that listed the token may cancel — BE enforces this.
export async function cancelListingEvm(
  tokenId: number
): Promise<{ success: true; tokenId: number; txHash: string }> {
  const response = await fetch(`${BACKEND_URL}/api/marketplace/evm/cancel`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      authorization: `Bearer ${getAuthToken()}`
    },
    body: JSON.stringify({ tokenId })
  })
  if (!response.ok) await parseError(response)
  return response.json()
}

// POST /api/marketplace/evm/buy  (auth required)
// Price + seller are read by BE from the on-chain listing — FE only needs
// to pass tokenId. BE checks USDC allowance and throws a structured 403
// (MarketplaceApprovalError, kind='usdc-allowance') if insufficient.
export async function buyListingEvm(
  tokenId: number
): Promise<{ success: true; tokenId: number; txHash: string }> {
  const response = await fetch(`${BACKEND_URL}/api/marketplace/evm/buy`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      authorization: `Bearer ${getAuthToken()}`
    },
    body: JSON.stringify({ tokenId })
  })
  if (!response.ok) await parseError(response)
  return response.json()
}
